import {
  useEffect,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '../lib/utils';

type ButtonVariant = 'primary' | 'ghost' | 'outline' | 'danger' | 'subtle';
type ButtonSize = 'sm' | 'md';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--color-brand-fill)] text-white hover:brightness-110 active:brightness-95 shadow-sm disabled:bg-[var(--color-line-strong)]',
  outline:
    'border border-[var(--color-line)] bg-[var(--color-surface)] text-[var(--color-ink)] hover:bg-[var(--color-surface-2)]',
  ghost: 'text-[var(--color-ink-2)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]',
  subtle: 'bg-[var(--color-surface-2)] text-[var(--color-ink)] hover:brightness-95 dark:hover:brightness-125',
  danger: 'bg-[var(--color-moved)] text-white hover:brightness-110',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-[12px] gap-1.5 rounded-md',
  md: 'h-9 px-3 text-[13px] gap-2 rounded-lg',
};

export function Button({
  variant = 'outline',
  size = 'md',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center justify-center font-medium whitespace-nowrap transition-all',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand)]',
        'disabled:cursor-not-allowed disabled:opacity-55',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    />
  );
}

export function IconButton({
  className,
  label,
  active,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; active?: boolean }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors',
        'text-[var(--color-ink-2)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand)]',
        active && 'bg-[var(--color-brand-soft)] text-[var(--color-brand)]',
        className,
      )}
      {...props}
    />
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        'h-9 w-full rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-3 text-[13px]',
        'text-[var(--color-ink)] placeholder:text-[var(--color-ink-3)]',
        'focus:border-[var(--color-brand)] focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)]/25',
        className,
      )}
      {...props}
    />
  );
}

/** A few lines of free text. Same skin as `Input`, so a form reads as one. */
export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(
        'w-full resize-y rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-3 py-2',
        'text-[13px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-3)]',
        'focus:border-[var(--color-brand)] focus:ring-2 focus:ring-[var(--color-brand)]/25 focus:outline-none',
        className,
      )}
      {...props}
    />
  );
}

/**
 * A date field that opens its calendar when you click it.
 *
 * `type="date"` already gives the browser's own picker, and the browser only
 * opens it from the little calendar button — so a click anywhere else in the
 * field does nothing, which reads as the picker not existing. `showPicker`
 * makes the whole field the button.
 *
 * It has to be called from a real gesture and throws when it is not, so the
 * failure is swallowed: the field still types dates either way.
 */
export function DateInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  const open = (event: MouseEvent<HTMLInputElement>) => {
    try {
      event.currentTarget.showPicker();
    } catch {
      /* not allowed here; the calendar button still works */
    }
  };

  return <Input type="date" className={cn('cursor-pointer', className)} onClick={open} {...props} />;
}

/**
 * A text field with the values already in use offered behind it.
 *
 * A `<datalist>` was the obvious way to do this and is the reason this exists:
 * the browser filters its own options against what is already in the field, so
 * a field holding a complete value — which is every one the app has filled in
 * for you — offers exactly one suggestion, itself. That reads as there being no
 * list at all, and the only way to pick a different office was to delete the
 * text first.
 *
 * So the filtering is ours, and the state that matters is `query`: null means
 * the list was opened rather than typed into, and everything is shown.
 *
 * Free text is still the point — a role can be somewhere the company is not yet
 * — so this stays an input with a list attached, not a select.
 */
export function Combobox({
  value,
  onChange,
  options,
  label,
  placeholder,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  options: readonly string[];
  /** Names the field and its list, for assistive tech and for tests. */
  label: string;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);

  const shown =
    query === null ? options : options.filter((option) => option.toLowerCase().includes(query.toLowerCase()));

  const close = () => {
    setOpen(false);
    setQuery(null);
  };

  const choose = (option: string) => (event: MouseEvent<HTMLButtonElement>) => {
    /*
     * Belt and braces against the click landing somewhere else first: the
     * mousedown guard below keeps the input from blurring out of the widget,
     * and this stops a surrounding `<label>` forwarding the activation back to
     * the input, which would reopen the list on top of the value just picked.
     * Wrap this in `FieldGroup` rather than `Field` and the second case cannot
     * arise at all — which is what `FieldGroup` is for.
     */
    event.preventDefault();
    onChange(option);
    close();
  };

  return (
    <div
      className={cn('relative', className)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
    >
      <div className="flex">
        <Input
          value={value}
          placeholder={placeholder}
          aria-label={label}
          role="combobox"
          aria-expanded={open}
          autoComplete="off"
          onChange={(event) => {
            onChange(event.target.value);
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setQuery(null);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            // Shut the list without also shutting the dialog around it.
            if (event.key === 'Escape' && open) {
              event.stopPropagation();
              close();
            }
          }}
          className="rounded-r-none"
        />
        <button
          type="button"
          aria-label={`Show every ${label.toLowerCase()}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.preventDefault();
            if (open) close();
            else {
              setQuery(null);
              setOpen(true);
            }
          }}
          className={cn(
            'flex h-9 w-8 shrink-0 items-center justify-center rounded-r-lg border border-l-0',
            'border-[var(--color-line)] text-[var(--color-ink-3)] transition-colors',
            'hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]',
          )}
        >
          <ChevronDown size={13} className={cn(open && 'rotate-180')} />
        </button>
      </div>

      {/*
        The popup is deliberately unnamed. The combobox beside it already carries
        the name, and naming both means two elements answer to "Location" — which
        makes asking for the field by its label ambiguous.
      */}
      {open && shown.length > 0 ? (
        <ul
          role="listbox"
          className={cn(
            'absolute z-30 mt-1 max-h-44 w-full overflow-y-auto rounded-lg border',
            'border-[var(--color-line)] bg-[var(--color-surface)] py-1 shadow-[var(--shadow-panel)]',
          )}
        >
          {shown.map((option) => (
            <li key={option}>
              <button
                type="button"
                role="option"
                aria-selected={option === value}
                onMouseDown={(event) => event.preventDefault()}
                onClick={choose(option)}
                className={cn(
                  'block w-full truncate px-3 py-1.5 text-left text-[13px] text-[var(--color-ink)]',
                  'hover:bg-[var(--color-surface-2)]',
                  option === value && 'font-semibold',
                )}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[11px] font-semibold tracking-wide text-[var(--color-ink-2)] uppercase">
        {label}
      </span>
      {children}
      {hint ? <span className="block text-[11px] text-[var(--color-ink-3)]">{hint}</span> : null}
    </label>
  );
}

/**
 * The same field, for a control that brings its own label.
 *
 * `Field` wraps its children in a `<label>`, and a label labels *every*
 * labelable thing inside it — so a combobox ends up sharing one name between
 * its text input and its own open button, and asking for the field by name
 * becomes ambiguous. A composite widget labels itself; this only draws the
 * heading.
 */
export function FieldGroup({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="block space-y-1.5">
      <span className="text-[11px] font-semibold tracking-wide text-[var(--color-ink-2)] uppercase">
        {label}
      </span>
      {children}
      {hint ? <span className="block text-[11px] text-[var(--color-ink-3)]">{hint}</span> : null}
    </div>
  );
}

export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: 'neutral' | 'moved' | 'fresh' | 'leaving' | 'brand';
  className?: string;
  children: ReactNode;
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-[var(--color-surface-2)] text-[var(--color-ink-2)]',
    moved: 'bg-[var(--color-moved-soft)] text-[var(--color-moved)]',
    fresh: 'bg-[var(--color-fresh-soft)] text-[var(--color-fresh)]',
    leaving: 'bg-[var(--color-leaving-soft)] text-[var(--color-leaving)]',
    brand: 'bg-[var(--color-brand-soft)] text-[var(--color-brand)]',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded border border-[var(--color-line)] bg-[var(--color-surface-2)] px-1.5 py-0.5 font-sans text-[10px] font-semibold text-[var(--color-ink-2)]">
      {children}
    </kbd>
  );
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'max-w-md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[8vh] sm:pt-[12vh]">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/40 backdrop-blur-[2px]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'card-in relative flex max-h-[84vh] w-full flex-col rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] shadow-[var(--shadow-panel)]',
          width,
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[var(--color-line)] px-4 py-3">
          <div>
            <h2 className="text-[14px] font-semibold text-[var(--color-ink)]">{title}</h2>
            {description ? (
              <p className="mt-0.5 text-[12px] text-[var(--color-ink-2)]">{description}</p>
            ) : null}
          </div>
          <IconButton label="Close" onClick={onClose} className="-mt-1 -mr-1">
            <X size={15} />
          </IconButton>
        </div>
        {/* The body scrolls so a form still fits on a short phone screen. */}
        {children ? <div className="min-h-0 space-y-3 overflow-y-auto px-4 py-4">{children}</div> : null}
        {footer ? (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[var(--color-line)] px-4 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function EmptyState({ icon, title, hint }: { icon: ReactNode; title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <div className="flex size-10 items-center justify-center rounded-full bg-[var(--color-surface-2)] text-[var(--color-ink-3)]">
        {icon}
      </div>
      <p className="text-[13px] font-medium text-[var(--color-ink)]">{title}</p>
      {hint ? (
        <p className="max-w-[36ch] text-[12px] leading-relaxed text-[var(--color-ink-3)]">{hint}</p>
      ) : null}
    </div>
  );
}
